// R-131-FIN-DECISION-A — pure grid / transmission-tariff pathway preset.
// Substation + queue timing wired via R-131-FIN-DECISION-B / D.

import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import type { Pathway, PathwayPhase } from '../types';
import {
  DEFAULT_BASE_WACC_PCT,
  PLACEHOLDER_QUEUE_YEARS,
  addMonthsIso,
  annualMwhFromCf,
  buildUniformCapexOutflows,
  capacityKw,
  computeAnnualOpexUsd,
  mergePathway,
  pathwayIdFromTechnology,
  resolveFacilityLoadMw,
  resolveNameplateMw,
  resolveProjectStartDate,
  valueOfServedLoadRevenue,
  type PathwayPresetOptions,
} from './shared';
import { estimateSubstationCost } from '../grid/substationCost';
import { lookupTransmissionTariff } from '../grid/transmissionTariff';

/** Placeholder grid benchmarks — refined by R-131-FIN-DECISION-B tariff lookup. */
export const GRID_TARIFF_BENCHMARKS = {
  /**
   * Flat $/kW fallback used ONLY when the site carries no grid-study data
   * (voltage class, tie-line distance, ISO region). When those inputs are
   * present, `estimateSubstationCost` (DOE QER / FERC 2023 / BLS sourced)
   * supersedes this value — see `resolveGridInterconnect`.
   */
  substationUsdPerKw: 500,
  transmissionUpgradeUsdPerKw: 150,
  /*
   * Q8 / C3 — the DELIVERY tariff is no longer a flat constant.
   *
   * `transmissionTariffPerMwhUsd: 42`, `demandChargePerKwMonthUsd: 12` and
   * `energyChargePerMwhUsd: 48` used to live here: three national round numbers
   * standing in for a rate that varies by ISO and by customer class, and they
   * were the single largest term in the grid pathway's LCOE. The sourced tables
   * in `../grid/transmissionTariff.ts` (2025 OASIS / tariff filings, per ISO,
   * with a data-center-large class) existed the whole time and had no caller.
   *
   * `lookupTransmissionTariff` is now the ONLY source of these three rates. It
   * falls back to PJM industrial for an unknown ISO, so there is still a
   * defined answer without site data — but it is a NAMED, CITED row rather
   * than an anonymous constant, and the citation travels with it.
   */
  /**
   * Grid-import operating emissions intensity, tCO2e/MWh. Represents the
   * delivered-grid carbon factor (US national average proxy ≈ 0.35 tCO2e/MWh).
   * Single source of truth for BOTH the standalone grid pathway carbon block
   * AND the grid-tariff MIX component in `blendPhaseEconomics`, so the two never
   * diverge. ASSUMPTION (US-average grid mix) — not a site/ISO-specific factor.
   */
  gridEmissionsTCo2ePerMwh: 0.35,
  designLifeYears: 30,
  availabilityPct: 99,
  capacityFactorPct: 95,
  riskPremiumBps: 25,
  contingencyPct: 15,
} as const;

/**
 * Documented defaults applied when a specific site input is absent. Each is
 * a flagged ASSUMPTION (not a sourced value) surfaced to the caller so an
 * investment-grade reader knows the CAPEX was not parameterised on real data.
 *
 *   - Tie-line distance: 2 miles — typical "near-substation" greenfield siting
 *     assumption when no grid-study distance is provided. ASSUMPTION, not sourced.
 *   - Target voltage: 138 kV — DOE QER substation cost tables use 138 kV as the
 *     base (multiplier = 1.0); chosen as the neutral mid-tier default. ASSUMPTION.
 *   - ISO region: estimator falls back to a 0.15 generic contingency (see
 *     ISO_CONTINGENCY_PCT default in substationCost.ts) when unknown.
 */
const GRID_INTERCONNECT_DEFAULTS = {
  assumedTieLineMiles: 2,
  assumedVoltageKv: 138,
} as const;

/** 1 statute mile = 1.609344 km — exact, for km→miles boundary conversion. */
const KM_PER_MILE = 1.609344;

/**
 * Optional grid-study fields the preset reads off the site context when
 * present. Mirrors `GridData` (src/types/siteRag.ts): `substationDist` is in
 * KILOMETRES, `voltage` is a string like "230kV", `isoRegion` like "PJM".
 * Read defensively because `SiteContext` does not type `grid` directly.
 */
interface SiteGridInputs {
  substationDist?: number;
  voltage?: string;
  isoRegion?: string;
}

function readSiteGridInputs(siteContext: SiteContext): SiteGridInputs | undefined {
  const grid = (siteContext as unknown as { grid?: SiteGridInputs } | null)?.grid;
  return grid && typeof grid === 'object' ? grid : undefined;
}

/** Parse a voltage label ("230kV", "115 kV", "345") to kV, or undefined. */
function parseVoltageKv(voltage?: string): number | undefined {
  if (!voltage) return undefined;
  const match = /([0-9]+(?:\.[0-9]+)?)/.exec(voltage);
  if (!match) return undefined;
  const kv = Number(match[1]);
  return Number.isFinite(kv) && kv > 0 ? kv : undefined;
}

interface GridInterconnectResult {
  /** Substation + equipment CAPEX (pre-contingency), USD. */
  substationUsd: number;
  /** Interconnection tie-line CAPEX (pre-contingency), USD. */
  transmissionUpgradeUsd: number;
  /** Contingency the cashflow builder applies to baseUsd (percent). */
  contingencyPct: number;
  /** True when derived from the sourced estimator; false for the flat fallback. */
  sourced: boolean;
  /** Sources cited by the estimator (empty for the flat fallback). */
  sources: string[];
  /** Flagged assumptions used because a site input was absent. */
  assumptions: string[];
  /** ISO region the site declared, or undefined when it declared none. */
  isoRegion?: string;
}

/**
 * Resolve substation + interconnection CAPEX for the grid pathway.
 *
 * When the site carries grid-study data (voltage class, tie-line distance,
 * ISO region) the REAL sourced model is used:
 *   - DOE Quadrennial Energy Review (QER) — substation civil/transformer tables
 *   - FERC 2023 Report on Costs Associated With Federal Interconnection Reqs
 *   - ISO/RTO interconnection study contingency (PJM/MISO/ERCOT/CAISO 2023-24)
 *   - BLS PPI — electrical equipment & construction (2020→2025 uplift)
 * (see src/lib/finDecision/grid/substationCost.ts).
 *
 * When site data is ABSENT the preset falls back to the flat $/kW placeholder
 * UNCHANGED, so existing single-source / hybrid / ranking outputs are identical
 * (ADDITIVE-ONLY guarantee). Any default substituted for a missing input is
 * flagged in `assumptions`.
 */
function resolveGridInterconnect(
  capacityMw: number,
  siteContext: SiteContext,
): GridInterconnectResult {
  const b = GRID_TARIFF_BENCHMARKS;
  const grid = readSiteGridInputs(siteContext);

  const voltageKv = parseVoltageKv(grid?.voltage);
  const hasDistance = grid != null && typeof grid.substationDist === 'number' && grid.substationDist >= 0;
  const isoRegion = typeof grid?.isoRegion === 'string' ? grid.isoRegion : undefined;

  // No grid-study signal at all → preserve the legacy flat placeholder exactly.
  if (voltageKv == null && !hasDistance && isoRegion == null) {
    return {
      ...(isoRegion != null ? { isoRegion } : {}),
      substationUsd: capacityKw(capacityMw) * b.substationUsdPerKw,
      transmissionUpgradeUsd: capacityKw(capacityMw) * b.transmissionUpgradeUsdPerKw,
      contingencyPct: b.contingencyPct,
      sourced: false,
      sources: [],
      assumptions: [],
    };
  }

  // At least one site input is present → use the sourced estimator, filling
  // any missing input with a flagged default.
  const assumptions: string[] = [];

  const targetVoltageKv = voltageKv ?? GRID_INTERCONNECT_DEFAULTS.assumedVoltageKv;
  if (voltageKv == null) {
    assumptions.push(
      `Grid interconnect: no target voltage on site — assumed ${GRID_INTERCONNECT_DEFAULTS.assumedVoltageKv} kV (DOE QER base tier). ASSUMPTION, not site data.`,
    );
  }

  // GridData.substationDist is KILOMETRES; estimator expects statute MILES.
  const distanceToTieMiles = hasDistance
    ? (grid!.substationDist as number) / KM_PER_MILE
    : GRID_INTERCONNECT_DEFAULTS.assumedTieLineMiles;
  if (!hasDistance) {
    assumptions.push(
      `Grid interconnect: no tie-line distance on site — assumed ${GRID_INTERCONNECT_DEFAULTS.assumedTieLineMiles} miles. ASSUMPTION, not site data.`,
    );
  }

  const resolvedIso = isoRegion ?? 'UNKNOWN';
  if (isoRegion == null) {
    assumptions.push(
      'Grid interconnect: no ISO region on site — estimator uses its generic 15% contingency default.',
    );
  }

  const est = estimateSubstationCost({
    siteCapacityMW: capacityMw,
    targetVoltageKv,
    distanceToTieMiles,
    isoRegion: resolvedIso,
  });

  // Map estimator components onto the pathway CAPEX topology used by the
  // cashflow builder: substationUsd = everything except the tie-line; the
  // tie-line maps to transmissionUpgradeUsd. Both are PRE-contingency so the
  // builder applies contingency once on baseUsd (no double count). Derive the
  // ISO contingency % back out of the estimator total so it rides baseUsd.
  const transmissionUpgradeUsd = est.interconnectionTieLineUsd;
  const preContingency = est.totalUsd - est.contingencyUsd;
  const substationUsd = preContingency - transmissionUpgradeUsd;
  const contingencyPct =
    preContingency > 0 ? (est.contingencyUsd / preContingency) * 100 : b.contingencyPct;

  return {
    ...(isoRegion != null ? { isoRegion } : {}),
    substationUsd,
    transmissionUpgradeUsd,
    contingencyPct,
    sourced: true,
    sources: est.sources,
    assumptions,
  };
}

/**
 * Public, side-effect-free view of the grid interconnect CAPEX resolution.
 * Exposes whether the sourced estimator or the flat fallback was used, the
 * cited sources, and any flagged assumptions — for callers / tests that want
 * to surface provenance without re-deriving it from the Pathway.
 */
export function computeGridInterconnectCost(
  capacityMw: number,
  siteContext: SiteContext,
): GridInterconnectResult {
  return resolveGridInterconnect(capacityMw, siteContext);
}

export type { GridInterconnectResult };

export function presetFor(
  intent: ProjectIntent,
  siteContext: SiteContext,
  options?: PathwayPresetOptions,
): Pathway {
  // Facility LOAD (the datacenter grid demand) and the firm grid interconnection
  // NAMEPLATE that serves it. The interconnection is grossed up above the load
  // (≈ load × 1.18: ~97% capacity credit + 15% planning reserve, firm-only — the
  // grid supplies unlimited firm energy so only the firm constraint binds).
  // Substation / tie-line capex and the interconnection rating ride the NAMEPLATE;
  // the demand charge and delivered energy ride the LOAD (see below).
  const facilityLoadMw = resolveFacilityLoadMw(intent);
  const capacityMw = resolveNameplateMw(intent, 'grid-tariff');
  const startDate = resolveProjectStartDate(intent);
  const b = GRID_TARIFF_BENCHMARKS;

  const interconnect = resolveGridInterconnect(capacityMw, siteContext);
  const substationUsd = interconnect.substationUsd;
  const transmissionUpgradeUsd = interconnect.transmissionUpgradeUsd;
  const baseCapexUsd = substationUsd + transmissionUpgradeUsd;

  const constructionMonths = Math.round(PLACEHOLDER_QUEUE_YEARS.medianYears * 12);
  // DELIVERED ENERGY for a firm 24/7 baseload datacenter rides the LOAD at the
  // grid AVAILABILITY (the load runs 24/7 and is served whenever the grid is up),
  // NOT the interconnection nameplate at a 95% market capacity factor. A baseload
  // grid customer draws ≈ load × 8760 × availability — the CF here was a
  // market-dispatch proxy that understated delivered energy for a flat firm load
  // (and would balloon spuriously if applied to the grossed-up nameplate). Energy
  // charges (variable O&M) bill on this delivered energy.
  const annualMwh = annualMwhFromCf(facilityLoadMw, b.availabilityPct);

  // ALL-IN delivered energy charge, USD/MWh = transmission tariff + energy
  // charge (for PJM data-center-large seed: 11.8 + 44.5 = 56.3 USD/MWh, plus
  // $13.5/kW-month demand charge; distinto from document-sourced utility quotes
  // such as R5's $85/MWh energy + $12/kW-month demand). This is the TOTAL the
  // pathway pays per delivered MWh; it is split across two DISJOINT opex fields
  // below.
  //
  // ENERGY-CHARGE INVARIANT (R-131-FIN-DECISION-C): the cashflow engine charges
  // `variableOmPerMwhUsd + transmissionTariffPerMwhUsd`, so
  // `variableOmPerMwhUsd` must carry ONLY the pure energy component
  // (= energyPerMwh − transmissionTariffPerMwhUsd). Setting it to the all-in
  // `energyPerMwh` while ALSO populating `transmissionTariffPerMwhUsd`
  // double-charged the transmission tariff.
  // Q8 — the delivery rates come from the SOURCED ISO table, on the same region
  // the CAPEX estimator used. An unknown / undeclared ISO falls back to the
  // table's PJM-industrial row, which is a cited default rather than an
  // anonymous national constant.
  const tariff = lookupTransmissionTariff(interconnect.isoRegion ?? 'PJM', 'data-center-large');
  const energyPerMwh = tariff.transmissionTariffPerMwhUsd + tariff.energyChargePerMwhUsd;
  /** Pure energy component, USD/MWh — the tariff rides its own opex field. */
  const energyOnlyPerMwh = energyPerMwh - tariff.transmissionTariffPerMwhUsd;
  // Demand charge is billed on the facility LOAD peak at the meter (kW), NOT the
  // grossed-up interconnection nameplate — billing the firm headroom would
  // over-charge demand. Energy O&M rides delivered energy (load-based annualMwh).
  const demandAnnualUsd =
    capacityKw(facilityLoadMw) * tariff.demandChargePerKwMonthUsd * 12;
  const annualOpexUsd =
    computeAnnualOpexUsd(capacityMw, 0, energyPerMwh, 0, annualMwh) + demandAnnualUsd;

  const operationStart = addMonthsIso(startDate, constructionMonths);
  const operationMonths = b.designLifeYears * 12;

  const phases: PathwayPhase[] = [
    {
      kind: 'construction',
      startDate,
      durationMonths: constructionMonths,
      capexUsd: baseCapexUsd,
    },
    {
      kind: 'operation',
      startDate: operationStart,
      durationMonths: operationMonths,
      annualMwh,
      annualOpexUsd,
    },
  ];

  // Contingency rides baseUsd in the cashflow builder. When the sourced
  // estimator was used it carries the ISO-specific contingency; otherwise the
  // flat placeholder contingency (15%) is preserved unchanged.
  //
  // CAPEX INVARIANT (R-131-FIN-DECISION-C): `phases[0].capexUsd` (= baseCapexUsd)
  // is the ONLY spend basis the cashflow engine uses; `substationUsd` and
  // `transmissionUpgradeUsd` below are DESCRIPTIVE components that must sum
  // into it (substation + tie-line == baseCapexUsd, USD) and are never re-added
  // by the engine.
  const capex = {
    baseUsd: baseCapexUsd,
    contingencyPct: interconnect.contingencyPct,
    substationUsd,
    transmissionUpgradeUsd,
    outflowsByYear: buildUniformCapexOutflows(startDate, constructionMonths, baseCapexUsd),
  };

  const codYear =
    new Date(startDate).getUTCFullYear() + Math.ceil(constructionMonths / 12);

  const base: Pathway = {
    id: pathwayIdFromTechnology('grid-tariff'),
    name: 'Grid Interconnection + Transmission Tariff',
    classification: 'grid',
    technology: 'grid-tariff',
    capacityMW: capacityMw,
    facilityLoadMW: facilityLoadMw,
    phases,
    capex,
    opex: {
      fixedOmPerKwYearUsd: 0,
      // Energy component ONLY (USD/MWh). The engine adds
      // `transmissionTariffPerMwhUsd` on top → 44.5 + 11.8 = 56.3 = `energyPerMwh`
      // (PJM data-center-large seed), matching `annualOpexUsd` on the operation
      // phase above. See the ENERGY-CHARGE INVARIANT note beside `energyPerMwh`.
      variableOmPerMwhUsd: energyOnlyPerMwh,
      fuelCostPerMwhUsd: 0,
      transmissionTariffPerMwhUsd: tariff.transmissionTariffPerMwhUsd,
      demandChargePerKwMonthUsd: tariff.demandChargePerKwMonthUsd,
    },
    // VALUE-CONSISTENT NPV: the grid baseline is credited the SAME
    // value-of-served-load as every generation pathway (NOT 0). Previously this
    // was a pure-cost stream (ppaPrice=0), which made the grid NPV ~4x worse
    // purely from missing revenue even though grid LCOE is only ~1.45x SMR's —
    // an apples-to-oranges comparison. Crediting the same value per served MWh
    // makes the compare-NPV read as "savings vs serving the load", so the NPV
    // ranking tracks the LCOE ranking (cheaper ⇒ less-negative / higher NPV).
    // See VALUE_OF_SERVED_LOAD_USD_PER_MWH in shared.ts for the chosen value +
    // its assumption/source.
    revenue: valueOfServedLoadRevenue({
      termYears: b.designLifeYears,
      availabilityPct: b.availabilityPct,
    }),
    risk: {
      technologyMaturity: 'mature',
      regulatoryRisk: 'medium',
      fuelPriceRisk: 'medium',
      baseWaccPct: DEFAULT_BASE_WACC_PCT,
      riskPremiumBps: b.riskPremiumBps,
      queueDelayDistribution: { ...PLACEHOLDER_QUEUE_YEARS },
    },
    carbon: {
      tCo2ePerMwh: b.gridEmissionsTCo2ePerMwh,
    },
    lifetime: {
      designLifeYears: b.designLifeYears,
      decommissioningUsd: substationUsd * 0.05,
      decommissioningYear: codYear + b.designLifeYears,
    },
  };

  return mergePathway(base, options?.overrides);
}
